import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import ChooseRecipientMembershipPopup from 'src/app/ChooseRecipientMembershipPopup/ChooseRecipientMembershipPopup'
import CreateVirtualCardRecipientWarningPopup from 'src/app/CreateVirtualCardRecipientWarningPopup/CreateVirtualCardRecipientWarningPopup'
import { selectUpdateableSelectedVirtualCardListModels } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import LowBalanceAlertField from 'src/component-form/LowBalanceAlertField/LowBalanceAlertField'
import AttachmentsInputBox from 'src/components/AttachmentsInputBox/AttachmentsInputBox'
import BillPayRecipientInputBox from 'src/components/BillPayRecipientInputBox/BillPayRecipientInputBox'
import BillPayVendorNameInputBox from 'src/components/BillPayVendorNameInputBox/BillPayVendorNameInputBox'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CreditCardAndAmountInputBox from 'src/components/CreditCardAndAmountInputBox/CreditCardAndAmountInputBox'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import CurrencyError from 'src/components/CurrencyError/CurrencyError'
import CustomVirtualCardAddressBox, {
  CustomVirtualCardAddressFlatFields,
} from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import ExactPayBox from 'src/components/ExactPayBox/ExactPayBox'
import ExactPayTooltip from 'src/components/ExactPayTooltip/ExactPayTooltip'
import ExpenseCategoriesInputBox, {
  ExpenseCategoriesContext,
} from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoriesReadOnlyBox from 'src/components/ExpenseCategoriesReadOnlyBox/ExpenseCategoriesReadOnlyBox'
import NotesInputBox from 'src/components/NotesInputBox/NotesInputBox'
import PhysicalVirtualCardBox, {
  PhysicalVirtualCardAddressFlatFields,
} from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import ReadOnlyNotesBox from 'src/components/ReadOnlyNotesBox/ReadOnlyNotesBox'
import ReceiptsRequiredBox from 'src/components/ReceiptsRequiredBox/ReceiptsRequiredBox'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import StaticRecipientApproverBox from 'src/components/StaticRecipientApproverBox/StaticRecipientApproverBox'
import UserAndNameInputBox from 'src/components/UserAndNameInputBox/UserAndNameInputBox'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { validToIsRequired } from 'src/components/VirtualCardForm/validTo.helpers'
import { ExtendCurrency, OrganizationRoles, VirtualCardTypes } from 'src/enums'
import {
  FormFieldsValidation,
  useFlyoutDispatch,
  useFormFields,
  useKeyedDivRefs,
  useScrollToScrollGroupRefs,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { AutomationRuleMappingResponse } from 'src/models/automationRules.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { ExpenseDetailsModel } from 'src/models/transaction.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { useQueryIssuer } from 'src/queries/creditCard.queries'
import {
  AutomationRuleListSortField,
  useAutomationRuleList,
} from 'src/queries/organizations/automationRules.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryResponse,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  getOrganizationReceiptMgmtEnabled,
  getReceiptRules,
} from 'src/redux/actions/data/organizations.actions'
import { LocationState } from 'src/redux/createRootReducer'
import {
  selectNullSafeCurrencyFromState,
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCountryListModelsFromState } from 'src/redux/selectors/data/countrys.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { FormProps } from 'src/types'
import { extendDay } from 'src/utils/date.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { momentIsEmpty } from 'src/utils/validation/moment.validation'
import {
  numberIsBelowMin,
  numberIsEmpty,
} from 'src/utils/validation/number.validation'
import { valueIsEmpty } from 'src/utils/validation/recurrence.validation'
import {
  stringArrayIsEmpty,
  stringIsEmpty,
  stringIsValidEmail,
} from 'src/utils/validation/string.validation'
import styled, { css, CSSProp } from 'styled-components'

export type VirtualCardFlatFormValues = {
  displayName?: string
  userEmails?: string[]
  recipientFirstName?: string
  recipientLastName?: string
  creditCardId?: string
  balanceDollars?: number
  limitDollars?: number
  singleExactPay?: boolean
  notes?: string
  validToMoment?: Moment
  customBillingAddress?: boolean
  includePhysicalCard?: boolean
  physicalCustomShippingAddress?: boolean
  plasticFirstName?: string
  plasticLastName?: string
  currency?: ExtendCurrency
  recipient?: string
  recipients?: string[]
  receiptRulesExempt?: boolean
  // bill pay
  vendorName?: string
  billPayRecipient?: BillPayRecipient
  vendorEmail?: string
  invoiceNumber?: string
  invoiceDateMoment?: Moment
  lowBalanceAlertEnabled?: boolean
  lowBalanceThresholdDollars?: number
}

export enum BillPayRecipient {
  Vendor,
  Myself,
}

interface VirtualCardFormProps {
  attachmentsProps?: {
    sectionHeader?: React.ReactNode
    sectionSubheader?: React.ReactNode
    receiptAttachments: ReceiptAttachmentModel[]
    uploadingAttachmentThumbnails: Record<string, string>
    uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
    deleteAttachment: (id: string) => void
  }
  buttonText: React.ReactNode
  billPayShareButtonText?: React.ReactNode
  multiCardButtonText?: React.ReactNode
  creditCardProps?: {
    labelCreditCardsAsAccounts: boolean
    budgetGroupLabel?: React.ReactNode
    isFormDisabled: boolean
    creditCardOptions: CreditCardOption[]
    accountMessage?: React.ReactNode
    multipleCards?: boolean
  }
  onCreditCardChange?: (creditCardId: string) => void
  resetsOnMomentProps?: FormProps<Moment | undefined>
  resetsOnHelperText?: React.ReactElement
  currencyError?: boolean
  errorDetailMap?: ErrorDetailMap<string>
  initialFlatFields?: Partial<VirtualCardFlatFormValues>
  initialRecurrenceFields?: Partial<RecurrenceFormValues>
  loading: boolean
  negativeButton?: {
    loading: boolean
    text: React.ReactNode | string
    onClick: () => void
  }
  onSuccessfulSubmit: (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrence: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
    role?: OrganizationRoles,
  ) => void
  topLevelError?: string
  topText?: React.ReactNode
  updateProps?: {
    baseFlatFields: VirtualCardFlatFormValues
    baseRecurrenceFields?: Partial<RecurrenceFormValues>
    timezone: string
  }
  userFieldLabel: React.ReactNode
  userOptions?: UserOption[]
  recentUserOptions?: UserOption[]
  filterUserbyAssocationTypes?: UserAssociation[]
  // features
  creditCardEditable?: boolean
  customAddressEditable?: boolean
  notesEditable: boolean
  physicalCardEditable?: boolean
  recipientNamesEditable: boolean
  recurrenceEditable: boolean
  amountProps: {
    amountEditable: boolean
    amountEditableOptional?: boolean
    amountPlaceholder?: string
    amountValue?: number
  }
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  tooltip?: React.ReactElement
  isMulti?: boolean
  suggestedProps?: {
    showSuggested: boolean
    showOwnUserInSuggested?: boolean
  }
  isRequest?: boolean
  canUpdateVirtualCard?: boolean
  id?: string
  isRecurringPayment?: boolean
  unableToAddNewUser?: boolean
  typeofCard: VirtualCardTypes
  showCardApprovalSoftAlert?: boolean
  awaitingFinalApproval?: boolean
  awaitingFundingSourceOwnerApproval?: boolean
  cardApproverId?: string
  virtualCard?: VirtualCardListModel
  hideWasOnUpdate?: boolean
  explicitSelectNone?: boolean
  lockAmountOnly?: boolean
  billPayExactPayEnabled?: boolean
}

// enum for mapping scroll groups, order matches DOM order
enum VirtualCardFormInputBoxes {
  UserAndNameInputBox,
  BillPayVendorNameInputBox,
  BillPayRecipientInputBox,
  CreditCardAndAmountInputBox,
  LowBalanceAlertBox,
  ExactPayBox,
  CustomVirtualCardAddressBox,
  PhysicalVirtualCardBox,
  ExpenseCategoriesInputBox,
  ReceiptsRequiredBox,
  AttachmentsInputBox,
  NotesInputBox,
}

const InputBoxDiv = styled.div<{
  isDisabled?: boolean
  isLocked?: boolean
  isGreyed?: boolean
  isError?: boolean
}>`
  &:nth-child(n + 2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    &:nth-child(2) {
      margin-top: ${styleConstants.heights.rightFlyoutPaddingXSMDown};
    }
  `)}

  ${({ isDisabled, isLocked, isGreyed }): CSSProp => {
    return isDisabled || isGreyed
      ? css`
          opacity: 0.5;
          pointer-events: none;
        `
      : isLocked
        ? css`
            pointer-events: none;
          `
        : css``
  }}

  ${({ isError }): CSSProp => {
    return isError
      ? css`
          border: 1px solid ${styleConstants.colors.red};
          border-radius: ${styleConstants.borderRadius.box};
        `
      : css``
  }}
`

const TopTextSpan = styled.span<{ warning?: boolean; warningBold?: boolean }>`
  ${getFontCss(
    12,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
  ${({ warning, warningBold }): CSSProp => {
    return warning
      ? css`
          ${getFontCss(
            12,
            styleConstants.colors.red,
            styleConstants.fontFamilies.regular,
          )};
        `
      : warningBold
        ? css`
            ${getFontCss(
              12,
              styleConstants.colors.red,
              styleConstants.fontFamilies.medium,
            )};
          `
        : css``
  }}
`

export const BillPaySanctionsScreeningNote = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <div
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
        padding-top: 24px;
      `}
    >
      {translate(
        '${bold} Your bank may ask the vendor for some additional information before the card can be used for payment.',
        {
          bold: (
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Please note:')}
            </span>
          ),
        },
      )}
    </div>
  )
}

const VirtualCardForm: React.FC<VirtualCardFormProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const location = useLocation<LocationState>()
  const userEmailMap = useSelector(selectUserEmailMap)
  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const countryListModels = useSelector(selectCountryListModelsFromState)
  const user = useSelector(selectUserListModelFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const updateableCards = useSelector(
    selectUpdateableSelectedVirtualCardListModels,
  )
  const bulkRecurringUpdate =
    updateableCards.filter((vc) => vc.recurs).length > 1
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const orgReceiptRulesEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: organization?.id,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const showGuestCopyMemberBK = showGuestRole && !user?.hasOrgAdminPermissions
  const showGuestCopyAdminOwner = showGuestRole && user?.hasOrgAdminPermissions

  const { data: organizationExpenseCategories } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })
  const { data: virtualCardRules } = useAutomationRuleList({
    filters: {
      sortField: AutomationRuleListSortField.CreatedAt,
      sortDirection: 'DESC',
      fundingSourceIds: [props.virtualCard?.id ?? ''],
    },
    enabled: props.virtualCard?.id !== undefined,
  })
  // TODO: currently assuming only one rule per VCN
  const virtualCardRule = useMemo(
    () => virtualCardRules?.[0],
    [virtualCardRules],
  )

  const dispatch = useFlyoutDispatch()

  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  const guestCardTypes = [
    VirtualCardTypes.guestAccess,
    VirtualCardTypes.recurringGuest,
  ]

  useEffect((): void => {
    if (
      organization?.id !== undefined &&
      organization?.receiptRule === undefined &&
      orgReceiptRulesEnabled
    ) {
      dispatch(getReceiptRules(organization.id))
    }
  }, [organization?.id, orgReceiptRulesEnabled])

  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const hasIntegrations = activeIntegration !== undefined

  const [selectedUsersNotInOrg, setSelectedUsersNotInOrg] = useState<string[]>(
    [],
  )

  const initialSelectedCreditCard: CreditCardListModel | undefined =
    props.initialFlatFields?.creditCardId !== undefined
      ? creditCards[props.initialFlatFields.creditCardId]
      : undefined

  const isDisabled = !!props.creditCardProps?.isFormDisabled
  const isLockedNonCardApproverForm =
    !!props.awaitingFinalApproval && user?.id !== props.cardApproverId
  const isFinalCardApproverForm =
    !!props.awaitingFinalApproval && user?.id === props.cardApproverId
  const isLocked = isFinalCardApproverForm
  const isGreyed = isLockedNonCardApproverForm
  const isUpdateForm = props.updateProps !== undefined
  const isApproveUpdateForm =
    !props.virtualCard?.isPending &&
    props.virtualCard?.pending !== undefined &&
    isUpdateForm &&
    props.canUpdateVirtualCard &&
    !isLockedNonCardApproverForm
  const isApproveRequestForm =
    (!!props.virtualCard?.isPending ||
      props.awaitingFundingSourceOwnerApproval) &&
    !isLockedNonCardApproverForm &&
    props.virtualCard?.cardholderId === user?.id
  const isRequestForm =
    !!props.isRequest && !isApproveRequestForm && !isApproveUpdateForm

  const isBillPay = props.typeofCard === VirtualCardTypes.billPay
  const isIssuerCycle = props.typeofCard === VirtualCardTypes.issuerCycle
  const isBulkUpdate = props.typeofCard === VirtualCardTypes.bulk
  const isCreateBillPay = isBillPay && !isUpdateForm && !isFinalCardApproverForm
  const isUpdateBillPay = isBillPay && !isCreateBillPay

  const disableCreditAndAmountInputBox =
    (!!props.virtualCard?.features?.singleExactPay && isUpdateForm) ||
    isDisabled
  const disableExactPayBox =
    (!isApproveRequestForm && isUpdateForm) || isDisabled
  const showVirtualCardRecipientWarningPopup =
    selectedUsersNotInOrg.length > 0 &&
    !guestCardTypes.includes(props.typeofCard) &&
    !isUpdateForm
  const showNotesBox =
    isLockedNonCardApproverForm || isFinalCardApproverForm
      ? props.updateProps?.baseFlatFields?.notes !== undefined
      : props.notesEditable
  const showAttachmentsBox =
    isLockedNonCardApproverForm || isFinalCardApproverForm
      ? (props?.attachmentsProps?.receiptAttachments?.length ?? 0) > 0
      : props.attachmentsProps !== undefined
  const [
    isCreateVirtualCardRecipientWarningPopupOpen,
    setIsCreateVirtualCardRecipientWarningPopupOpen,
  ] = useState(false)
  const [initialExpenseCategoryObjects, setInitialExpenseCategoryObjects] =
    useState<Record<string, Array<ExpenseDetailsModel | undefined>>>({})

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: validateFlatFields,
    setFieldsErrors: setFlatFieldsErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
    setTopmostScrollGroup: setFlatFieldsTopmostScrollGroup,
    setFields: setFlatFields,
  } = useFormFields<VirtualCardFlatFormValues>(
    {
      vendorName: props.initialFlatFields?.vendorName || undefined,
      displayName:
        props.updateProps?.baseFlatFields.displayName ||
        props.initialFlatFields?.displayName ||
        undefined,
      userEmails: location.state?.autofill
        ? [location.state?.autofill]
        : undefined,
      recipientFirstName:
        props.updateProps?.baseFlatFields.recipientFirstName ||
        props.initialFlatFields?.recipientFirstName ||
        undefined,
      recipientLastName:
        props.updateProps?.baseFlatFields.recipientLastName ||
        props.initialFlatFields?.recipientLastName ||
        undefined,
      creditCardId:
        props.updateProps?.baseFlatFields.creditCardId ||
        props.initialFlatFields?.creditCardId ||
        undefined,
      balanceDollars:
        props.amountProps.amountEditable &&
        !props.amountProps?.amountEditableOptional
          ? 0
          : props.typeofCard === VirtualCardTypes.bulk
            ? props.amountProps.amountValue
            : undefined,
      limitDollars:
        props.updateProps?.baseFlatFields.limitDollars ||
        props.initialFlatFields?.limitDollars ||
        undefined,
      validToMoment: !props.amountProps?.amountEditableOptional
        ? moment().add(3, 'month')
        : undefined,
      singleExactPay: props.virtualCard?.features?.singleExactPay || undefined,
      notes: undefined,
      currency: initialSelectedCreditCard?.currency || defaultCurrency,
      receiptRulesExempt:
        props.updateProps?.baseFlatFields.receiptRulesExempt ||
        props.initialFlatFields?.receiptRulesExempt ||
        undefined,
      lowBalanceAlertEnabled:
        props.updateProps?.baseFlatFields.lowBalanceAlertEnabled ??
        props.initialFlatFields?.lowBalanceAlertEnabled ??
        false,
      lowBalanceThresholdDollars:
        props.updateProps?.baseFlatFields.lowBalanceThresholdDollars ??
        props.initialFlatFields?.lowBalanceThresholdDollars ??
        undefined,
      ...props.initialFlatFields,
    },
    (
      flatFields: VirtualCardFlatFormValues,
    ): FormFieldsValidation<VirtualCardFlatFormValues> => {
      const userHasName =
        flatFields.userEmails !== undefined &&
        flatFields?.userEmails?.length === 1
          ? userEmailMap[flatFields.userEmails[0]]?.hasFullName
          : undefined
      return {
        displayName: props.userOptions ? [stringIsEmpty()] : undefined,
        userEmails: props.userOptions ? [stringArrayIsEmpty] : undefined, // TODO: optional validation
        plasticFirstName:
          flatFields.includePhysicalCard && !userHasName
            ? [stringIsEmpty()]
            : undefined,
        plasticLastName:
          flatFields.includePhysicalCard && !userHasName
            ? [stringIsEmpty()]
            : undefined,
        recipientFirstName:
          props.userOptions && props.recipientNamesEditable && !userHasName
            ? [stringIsEmpty()]
            : undefined,
        recipientLastName:
          props.userOptions && props.recipientNamesEditable && !userHasName
            ? [stringIsEmpty()]
            : undefined,
        creditCardId:
          props.amountProps.amountEditable &&
          props.creditCardProps !== undefined &&
          !props.creditCardProps.multipleCards
            ? [stringIsEmpty()]
            : undefined,
        balanceDollars: props.amountProps.amountEditable
          ? props.amountProps.amountEditableOptional
            ? [numberIsBelowMin(1)]
            : [numberIsEmpty(), numberIsBelowMin(1)]
          : undefined,
        // currently just used for bill pay as a read-only value, balanceDollars is also used as the limit in the create flow
        limitDollars: undefined,
        validToMoment: validToIsRequired(props.recurrenceEditable, isBulkUpdate)
          ? [momentIsEmpty()]
          : undefined,
        notes: [],
        currency: [stringIsEmpty()],
        recipient: undefined,
        recipients: undefined,
        receiptRulesExempt: undefined,
        // bill pay
        vendorName: isCreateBillPay ? [stringIsEmpty()] : undefined,
        billPayRecipient: isCreateBillPay
          ? [valueIsEmpty('Must make a selection')]
          : undefined,
        vendorEmail:
          isCreateBillPay &&
          flatFields.billPayRecipient === BillPayRecipient.Vendor
            ? [stringIsValidEmail()]
            : undefined,
        lowBalanceThresholdDollars: flatFields.lowBalanceAlertEnabled
          ? [numberIsEmpty(), numberIsBelowMin(1)]
          : undefined,
      }
    },
    props.errorDetailMap,
    undefined,
    {
      displayName: VirtualCardFormInputBoxes.UserAndNameInputBox,
      userEmails: VirtualCardFormInputBoxes.UserAndNameInputBox,
      recipientFirstName: VirtualCardFormInputBoxes.UserAndNameInputBox,
      recipientLastName: VirtualCardFormInputBoxes.UserAndNameInputBox,
      vendorName: VirtualCardFormInputBoxes.BillPayVendorNameInputBox,
      billPayRecipient: VirtualCardFormInputBoxes.BillPayRecipientInputBox,
      vendorEmail: VirtualCardFormInputBoxes.BillPayRecipientInputBox,
      creditCardId: VirtualCardFormInputBoxes.CreditCardAndAmountInputBox,
      balanceDollars: VirtualCardFormInputBoxes.CreditCardAndAmountInputBox,
      currency: VirtualCardFormInputBoxes.CreditCardAndAmountInputBox,
      validToMoment: VirtualCardFormInputBoxes.CreditCardAndAmountInputBox,
      physicalCustomShippingAddress:
        VirtualCardFormInputBoxes.CustomVirtualCardAddressBox,
      plasticFirstName: VirtualCardFormInputBoxes.PhysicalVirtualCardBox,
      plasticLastName: VirtualCardFormInputBoxes.PhysicalVirtualCardBox,
      receiptRulesExempt: VirtualCardFormInputBoxes.ReceiptsRequiredBox,
      lowBalanceThresholdDollars: VirtualCardFormInputBoxes.LowBalanceAlertBox,
    },
  )

  const selectedUser: UserListModel | undefined =
    flatFields.userEmails !== undefined && flatFields?.userEmails?.length === 1
      ? userEmailMap[flatFields.userEmails[0]]
      : undefined
  const selectedUserHasName = selectedUser?.hasFullName

  const selectedCreditCard: CreditCardListModel | undefined =
    flatFields?.creditCardId !== undefined
      ? creditCards[flatFields.creditCardId]
      : props.virtualCard?.creditCardId !== undefined
        ? creditCards[props.virtualCard.creditCardId]
        : undefined

  let labelCreditCardsAsAccounts: boolean | undefined =
    props.creditCardProps?.labelCreditCardsAsAccounts
  if (
    labelCreditCardsAsAccounts === undefined &&
    selectedCreditCard !== undefined
  ) {
    labelCreditCardsAsAccounts = !selectedCreditCard.isBudget
  }

  const isIntegerOnly =
    isIssuerCycle ||
    selectedCreditCard?.issuerCycleConfig != null ||
    props.virtualCard?.issuerCycleConfig != null

  const { data: selectedIssuer } = useQueryIssuer({
    issuerId: selectedCreditCard?.issuerId,
    enabled: isBillPay,
  })

  const isRecurring = !!props.isRecurringPayment
  const currencyValue = flatFields.currency
  const currencyBaseValue = props.updateProps?.baseFlatFields.currency
  const isInvalidCurrencyUpdateRequest =
    !!isApproveRequestForm && currencyBaseValue !== currencyValue

  const countrySchema =
    selectedCreditCard !== undefined &&
    countryListModels[selectedCreditCard.address.country] !== undefined
      ? countryListModels[selectedCreditCard.address.country].fields
      : undefined

  const customAddressFieldsValidation =
    (): FormFieldsValidation<CustomVirtualCardAddressFlatFields> =>
      countrySchema !== undefined &&
      flatFields.customBillingAddress &&
      props.customAddressEditable
        ? countrySchema.reduce((acc, curr) => {
            if (!curr.optional) {
              return { ...acc, [curr.name]: [stringIsEmpty()] }
            } else {
              return acc
            }
          }, {})
        : {}
  const {
    fields: customAddressFields,
    fieldsErrors: customAddressFieldsErrors,
    onFieldChange: onCustomAddressFieldChange,
    setErrorsAndReturnDidPassValidation: customAddressFieldsSetErrors,
    topmostScrollGroup: customAddressFieldsTopmostScrollGroup,
    setTopmostScrollGroup: setCustomAddressFieldsTopmostScrollGroup,
  } = useFormFields<CustomVirtualCardAddressFlatFields>(
    {},
    customAddressFieldsValidation,
    props.errorDetailMap,
    undefined,
    VirtualCardFormInputBoxes.CustomVirtualCardAddressBox,
  )

  const physicalAddressFieldsValidation =
    (): FormFieldsValidation<CustomVirtualCardAddressFlatFields> =>
      countrySchema !== undefined &&
      flatFields.includePhysicalCard &&
      flatFields.physicalCustomShippingAddress &&
      props.customAddressEditable
        ? countrySchema.reduce((acc, curr) => {
            if (!curr.optional) {
              return { ...acc, [curr.name]: [stringIsEmpty()] }
            } else {
              return acc
            }
          }, {})
        : {}

  const {
    fields: physicalAddressFields,
    fieldsErrors: physicalAddressFieldsErrors,
    onFieldChange: onPhysicalAddressFieldChange,
    setErrorsAndReturnDidPassValidation: physicalAddressFieldsSetErrors,
    topmostScrollGroup: physicalAddressFieldsTopmostScrollGroup,
    setTopmostScrollGroup: setPhysicalAddressFieldsTopmostScrollGroup,
  } = useFormFields<CustomVirtualCardAddressFlatFields>(
    {},
    physicalAddressFieldsValidation,
    props.errorDetailMap,
  )

  const {
    fields: recurrenceFields,
    fieldsErrors: recurrenceFieldsErrors,
    onFieldChange: onRecurrenceFieldChange,
    setErrorsAndReturnDidPassValidation: recurrenceFieldsSetErrors,
    topmostScrollGroup: recurrenceFieldsTopmostScrollGroup,
    setTopmostScrollGroup: setRecurrenceFieldsTopmostScrollGroup,
  } = useFormFields<RecurrenceFormValues>(
    {
      amountDollars:
        flatFields.balanceDollars !== undefined
          ? flatFields.balanceDollars
          : bulkRecurringUpdate
            ? undefined
            : 0,
      recurrenceDollars:
        props.initialRecurrenceFields?.recurrenceDollars !== undefined
          ? props.initialRecurrenceFields.recurrenceDollars
          : undefined,
      period:
        props.initialRecurrenceFields?.period !== undefined
          ? props.initialRecurrenceFields.period
          : bulkRecurringUpdate
            ? undefined
            : RecurrencePeriod.Weekly,
      interval:
        props.initialRecurrenceFields?.interval !== undefined
          ? props.initialRecurrenceFields.interval
          : bulkRecurringUpdate
            ? undefined
            : 1,
      terminator:
        props.initialRecurrenceFields?.terminator !== undefined
          ? props.initialRecurrenceFields.terminator
          : bulkRecurringUpdate
            ? undefined
            : RecurrenceTerminator.Count,
      count:
        props.initialRecurrenceFields?.count !== undefined
          ? props.initialRecurrenceFields.count
          : bulkRecurringUpdate
            ? undefined
            : 10,
      untilMoment:
        props.initialRecurrenceFields?.untilMoment !== undefined
          ? props.initialRecurrenceFields.untilMoment
          : bulkRecurringUpdate
            ? undefined
            : moment().add(1, 'month'),
      byWeekDay:
        props.initialRecurrenceFields?.byWeekDay !== undefined
          ? props.initialRecurrenceFields?.byWeekDay
          : bulkRecurringUpdate
            ? undefined
            : extendDay(),
      byMonthDay:
        props.initialRecurrenceFields?.byMonthDay !== undefined
          ? props.initialRecurrenceFields?.byMonthDay
          : bulkRecurringUpdate
            ? undefined
            : moment().date(),
      byYearDay:
        props.initialRecurrenceFields?.byYearDay !== undefined
          ? props.initialRecurrenceFields?.byYearDay
          : bulkRecurringUpdate
            ? undefined
            : moment().date(),
    },
    props.recurrenceEditable
      ? (fields): FormFieldsValidation<RecurrenceFormValues> => ({
          recurrenceDollars: isUpdateForm
            ? !bulkRecurringUpdate
              ? [numberIsEmpty(), numberIsBelowMin(1)]
              : [numberIsBelowMin(1)]
            : [],
          period: !bulkRecurringUpdate ? [valueIsEmpty()] : [],
          interval: !bulkRecurringUpdate
            ? [numberIsEmpty(), numberIsBelowMin(1)]
            : [numberIsBelowMin(1)],
          terminator: !bulkRecurringUpdate ? [valueIsEmpty()] : [],
          count:
            !bulkRecurringUpdate &&
            fields.terminator === RecurrenceTerminator.Count
              ? [
                  numberIsEmpty(),
                  ...(isUpdateForm ? [] : [numberIsBelowMin(1)]),
                ]
              : [],
          untilMoment:
            !bulkRecurringUpdate &&
            (fields.terminator === RecurrenceTerminator.Count ||
              fields.terminator === RecurrenceTerminator.Date)
              ? [valueIsEmpty()]
              : [],
          byWeekDay:
            !bulkRecurringUpdate && fields.period === RecurrencePeriod.Weekly
              ? [valueIsEmpty()]
              : [],
          byMonthDay:
            !bulkRecurringUpdate && fields.period === RecurrencePeriod.Monthly
              ? [valueIsEmpty()]
              : [],
        })
      : (): FormFieldsValidation<RecurrenceFormValues> => ({}),
    undefined,
    undefined,
    VirtualCardFormInputBoxes.CreditCardAndAmountInputBox,
  )

  const expenseCategorySchema = useMemo(():
    | ExpenseCategoryResponse[]
    | undefined => {
    let schema: ExpenseCategoryResponse[] | undefined
    if (organizationExpenseCategories !== undefined) {
      schema = Object.values(organizationExpenseCategories)
    }

    if (schema !== undefined && !!props.explicitSelectNone) {
      // treat all fields as optional for bulk updates
      schema = schema.map((expCat) => ({
        ...expCat,
        requiredForVirtualCards: false,
      }))
    }

    return schema
  }, [organizationExpenseCategories, props.explicitSelectNone])

  const virtualCardExpenseCategoriesValidation =
    expenseCategorySchema !== undefined && !isRequestForm
      ? (): FormFieldsValidation<Record<string, string>> =>
          expenseCategorySchema.reduce((acc, curr) => {
            // Account is not treated differently for VCs
            return curr.requiredForVirtualCards
              ? { ...acc, [curr.id]: [stringIsEmpty()] }
              : acc
          }, {})
      : (): FormFieldsValidation<Record<string, string>> => ({})

  const formatRuleMappingsToSelected = useCallback(
    (mappings: AutomationRuleMappingResponse[]): Record<string, string> => {
      return mappings.reduce((acc, mapping) => {
        const matchingCategory = expenseCategorySchema?.find(
          (cat) => cat.id === mapping.expenseCategory.id,
        )

        if (
          mapping.expenseLabel === undefined ||
          matchingCategory === undefined
        )
          return acc

        if (
          matchingCategory.type === ExpenseCategoryType.Vendor ||
          matchingCategory.type === ExpenseCategoryType.Customer
        ) {
          return {
            ...acc,
            [matchingCategory.id]: mapping.expenseLabel.code,
          }
        } else {
          return {
            ...acc,
            // if id is empty or undefined, use the name to handle free text values
            [matchingCategory.id]:
              mapping.expenseLabel.id || mapping.expenseLabel.name,
          }
        }
      }, {})
    },
    [expenseCategorySchema],
  )

  const formatRuleMappingsToExpCatObjects = useCallback(
    (
      mappings: AutomationRuleMappingResponse[],
    ): Record<string, ExpenseDetailsModel[]> => {
      return mappings.reduce((acc, curr) => {
        if (curr.expenseLabel === undefined) return acc
        return {
          ...acc,
          [curr.expenseCategory.id]: [
            {
              categoryId: curr.expenseCategory.id,
              categoryName: curr.expenseCategory.name,
              categoryType: curr.expenseCategory.type,
              labelId:
                curr.expenseCategory.type === ExpenseCategoryType.Customer ||
                curr.expenseCategory.type === ExpenseCategoryType.Vendor
                  ? curr.expenseLabel.code
                  : curr.expenseLabel.id,
              labelName: curr.expenseLabel.name,
              subLabelName: curr.expenseLabel.subName,
              generatedByPrediction: false,
            } satisfies ExpenseDetailsModel,
          ],
        }
      }, {})
    },
    [],
  )

  const initExpCatsForRules = useMemo(() => {
    return virtualCardRule?.mappings
      ? formatRuleMappingsToSelected(virtualCardRule.mappings)
      : {}
  }, [virtualCardRule?.mappings, formatRuleMappingsToSelected])

  const initExpCatObjectsForRules = useMemo(
    () =>
      virtualCardRule?.mappings
        ? formatRuleMappingsToExpCatObjects(virtualCardRule.mappings)
        : {},
    [virtualCardRule?.mappings, formatRuleMappingsToExpCatObjects],
  )

  const {
    fields: expenseCategories,
    setFields: setExpenseCategories,
    fieldsErrors: expenseCategoriesErrors,
    onFieldChange: onExpenseCategoryChange,
    setErrorsAndReturnDidPassValidation: expenseCategoriesSetErrors,
    topmostScrollGroup: expenseCategoriesTopmostScrollGroup,
    setTopmostScrollGroup: setExpenseCategoriesTopmostScrollGroup,
  } = useFormFields<Record<string, string>>(
    formatRuleMappingsToSelected(virtualCardRule?.mappings ?? []),
    virtualCardExpenseCategoriesValidation,
    undefined,
    undefined,
    VirtualCardFormInputBoxes.ExpenseCategoriesInputBox,
  )

  const showExpenseCategories =
    // disable expense categories for bulk until rules update
    props.typeofCard !== VirtualCardTypes.bulk &&
    (isLocked || isGreyed
      ? expenseCategories !== undefined &&
        Object.entries(expenseCategories).length > 0
      : organizationExpenseCategories !== undefined &&
        (expenseCategorySchema?.length ?? 0) > 0)

  useEffect(() => {
    setExpenseCategories(initExpCatsForRules)
    setInitialExpenseCategoryObjects(initExpCatObjectsForRules)
  }, [initExpCatsForRules, initExpCatObjectsForRules, virtualCardRules])

  const inputBoxRefs = useKeyedDivRefs(VirtualCardFormInputBoxes)

  useScrollToScrollGroupRefs(
    inputBoxRefs ? Object.values(inputBoxRefs) : [],
    [
      flatFieldsTopmostScrollGroup,
      recurrenceFieldsTopmostScrollGroup,
      expenseCategoriesTopmostScrollGroup,
      customAddressFieldsTopmostScrollGroup,
      physicalAddressFieldsTopmostScrollGroup,
    ],
    [
      setFlatFieldsTopmostScrollGroup,
      setRecurrenceFieldsTopmostScrollGroup,
      setCustomAddressFieldsTopmostScrollGroup,
      setPhysicalAddressFieldsTopmostScrollGroup,
      setExpenseCategoriesTopmostScrollGroup,
    ],
  )

  const submitFunction = (role?: OrganizationRoles): void =>
    props.onSuccessfulSubmit(
      {
        ...flatFields,
        recipientFirstName:
          flatFields?.userEmails?.length === 1
            ? flatFields.recipientFirstName || selectedUser?.firstName
            : undefined,
        recipientLastName:
          flatFields?.userEmails?.length === 1
            ? flatFields.recipientLastName || selectedUser?.lastName
            : undefined,
        plasticFirstName:
          flatFields?.userEmails?.length === 1
            ? flatFields.plasticFirstName || selectedUser?.firstName
            : undefined,
        plasticLastName:
          flatFields?.userEmails?.length === 1
            ? flatFields.plasticLastName || selectedUser?.lastName
            : undefined,
      },
      expenseCategories,
      recurrenceFields,
      {
        ...customAddressFields,
        country: selectedCreditCard?.address.country,
      },
      {
        ...physicalAddressFields,
        country: selectedCreditCard?.address.country,
      },
      role,
    )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsUpdated = Object.values(flatFields).some(
      (item) => item !== undefined,
    )
    const recurrenceFieldsUpdated = Object.values(recurrenceFields).some(
      (item) => item !== undefined,
    )

    const flatFieldsPassedValidation = props.amountProps?.amountEditableOptional
      ? flatFieldsUpdated
        ? validateFlatFields()
        : true
      : validateFlatFields()

    const recurrenceFieldsPassedValidation = props.amountProps
      ?.amountEditableOptional
      ? recurrenceFieldsUpdated
        ? recurrenceFieldsSetErrors()
        : true
      : recurrenceFieldsSetErrors()

    const expenseCategoriesPassedValidation = expenseCategoriesSetErrors()
    const customAddressFieldsPassedValidation = customAddressFieldsSetErrors()
    const physicalAddressFieldsPassedValidation =
      physicalAddressFieldsSetErrors()
    if (
      flatFieldsPassedValidation &&
      recurrenceFieldsPassedValidation &&
      expenseCategoriesPassedValidation &&
      customAddressFieldsPassedValidation &&
      physicalAddressFieldsPassedValidation
    ) {
      if (
        (showGuestRole || user?.hasOrgAdminPermissions) &&
        showVirtualCardRecipientWarningPopup
      ) {
        setIsCreateVirtualCardRecipientWarningPopupOpen(true)
      } else {
        submitFunction(OrganizationRoles.guest)
      }
    }
  }

  // get org receipt rules enabled flag if undefined
  useEffect((): void => {
    if (
      organization !== undefined &&
      !props.isRequest &&
      user?.hasOrgBookkeeperPermissions &&
      orgReceiptRulesEnabled === undefined &&
      !props.virtualCard?.direct &&
      props.typeofCard !== VirtualCardTypes.bulk
    ) {
      dispatch(getOrganizationReceiptMgmtEnabled(organization.id))
    }
  }, [organization])

  const virtualCardCurrency = flatFields.currency || props.virtualCard?.currency

  const enableExactPay =
    !!selectedCreditCard?.features?.singleExactPayEnabled &&
    !props.recurrenceEditable &&
    (!isBillPay || !!props.billPayExactPayEnabled)

  const getBalanceInputLabel = (): React.ReactNode => {
    if (isBillPay) {
      return translate('Invoice Amount')
    } else if (isIssuerCycle) {
      return translate('Monthly Credit Limit')
    } else if (isUpdateForm && !isApproveRequestForm) {
      return translate('Available Credit')
    } else {
      return translate('Credit Limit')
    }
  }

  const getLimitInputLabel = (): React.ReactNode | undefined => {
    if (isBillPay) {
      return translate('Invoice Amount')
    } else if (props.lockAmountOnly) {
      return translate('Credit Limit')
    } else {
      return undefined
    }
  }

  const getButtonText = (): React.ReactNode | undefined => {
    if (props.isMulti && (flatFields.userEmails || []).length > 1) {
      return props.multiCardButtonText
    } else if (
      isBillPay &&
      flatFields.billPayRecipient === BillPayRecipient.Vendor
    ) {
      return props.billPayShareButtonText
    } else {
      return props.buttonText
    }
  }

  return (
    <>
      {isCreateVirtualCardRecipientWarningPopupOpen &&
        showVirtualCardRecipientWarningPopup &&
        (showGuestRole && user?.hasOrgAdminPermissions ? (
          <ChooseRecipientMembershipPopup
            selectedEmailsNotInOrg={selectedUsersNotInOrg}
            submitFunction={submitFunction}
            closeFunction={setIsCreateVirtualCardRecipientWarningPopupOpen}
          />
        ) : (
          <CreateVirtualCardRecipientWarningPopup
            selectedEmailsNotInOrg={selectedUsersNotInOrg}
            submitFunction={submitFunction}
            closeFunction={setIsCreateVirtualCardRecipientWarningPopupOpen}
          />
        ))}
      <form
        onSubmit={onSubmit}
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        {props.currencyError &&
          props.updateProps?.baseFlatFields.currency !== undefined && (
            <CurrencyError
              currency={props.updateProps.baseFlatFields.currency}
            />
          )}
        {props.topText !== undefined &&
          !props.currencyError &&
          !disableCreditAndAmountInputBox &&
          !isInvalidCurrencyUpdateRequest && (
            <TopTextSpan>{props.topText}</TopTextSpan>
          )}
        {!isDisabled && isInvalidCurrencyUpdateRequest && (
          <TopTextSpan warningBold>
            {translate(
              'You do not have any Funding Sources that support the requested currency (${requestedCurrency}). ',
              {
                requestedCurrency: currencyBaseValue || '',
              },
            )}
            <TopTextSpan warning>
              {translate(
                'You can approve this request by selecting an account or budget and entering a credit limit in your available currency. You may want to follow up with the Recipient.',
              )}
            </TopTextSpan>
          </TopTextSpan>
        )}
        {isDisabled &&
          (isRecurring ? (
            <TopTextSpan warningBold>
              {translate(
                'You do not have any funding sources that support the requested card type (Recurring). ',
              )}
              <TopTextSpan warning>
                {translate(
                  'Please press "Change Card Type" above to select a supported Virtual Card type. You may want to follow up with the Recipient and notify them.',
                )}
              </TopTextSpan>
            </TopTextSpan>
          ) : (
            <TopTextSpan warningBold>
              {translate(
                'You do not have an active account or budget to create this card.',
              )}
            </TopTextSpan>
          ))}
        {isFinalCardApproverForm ? (
          <InputBoxDiv
            css={css`
              z-index: 6;
            `}
            isDisabled={disableCreditAndAmountInputBox}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.UserAndNameInputBox]}
          >
            <StaticRecipientApproverBox
              displayNameProps={{
                baseValue: props.updateProps?.baseFlatFields.displayName,
                showUpdates: isUpdateForm,
                value: flatFields.displayName,
                errors: flatFieldsErrors.displayName,
                onChange: (val: string): void => {
                  onFlatFieldChange('displayName', val)
                },
              }}
              displayNameFieldLabel={
                props.typeofCard === VirtualCardTypes.billPay
                  ? undefined
                  : translate('Card Name')
              }
              userEmailProps={{
                value: flatFields.userEmails,
                errors:
                  flatFieldsErrors.userEmails ||
                  flatFieldsErrors.recipient ||
                  flatFieldsErrors.recipients,
                onChange: (val: string[]): void => {
                  onFlatFieldChange('userEmails', val)
                },
                isStatic: isUpdateForm,
              }}
              id={props.id === 'request' ? 'request' : 'recipient'}
              unableToAddNewUser={props.unableToAddNewUser}
              cardHolderEmail={props.virtualCard?.cardholder?.email}
              recipientEmail={
                props.typeofCard === VirtualCardTypes.billPay
                  ? undefined
                  : props.virtualCard?.recipient?.email
              }
            />
          </InputBoxDiv>
        ) : props.userOptions ? (
          <InputBoxDiv
            css={css`
              z-index: 6;
            `}
            isDisabled={isDisabled}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.UserAndNameInputBox]}
          >
            <UserAndNameInputBox
              displayNameProps={{
                baseValue: props.updateProps?.baseFlatFields.displayName,
                showUpdates: isUpdateForm,
                value: flatFields.displayName,
                errors: flatFieldsErrors.displayName,
                onChange: (val: string): void => {
                  onFlatFieldChange('displayName', val)
                },
              }}
              displayNameFieldLabel={translate('Card Name')}
              recipientFirstNameProps={
                props.recipientNamesEditable
                  ? {
                      value: flatFields.recipientFirstName,
                      errors: flatFieldsErrors.recipientFirstName,
                      onChange: (val: string): void => {
                        onFlatFieldChange('recipientFirstName', val)
                      },
                    }
                  : undefined
              }
              recipientLastNameProps={
                props.recipientNamesEditable
                  ? {
                      value: flatFields.recipientLastName,
                      errors: flatFieldsErrors.recipientLastName,
                      onChange: (val: string): void => {
                        onFlatFieldChange('recipientLastName', val)
                      },
                    }
                  : undefined
              }
              userFieldLabel={props.userFieldLabel}
              userEmailProps={{
                value: flatFields.userEmails,
                errors:
                  flatFieldsErrors.userEmails ||
                  flatFieldsErrors.recipient ||
                  flatFieldsErrors.recipients,
                onChange: (val: string[]): void => {
                  onFlatFieldChange('userEmails', val)
                },
                isStatic: isUpdateForm,
              }}
              userOptions={props.userOptions}
              recentUserOptions={props.recentUserOptions}
              filterUserbyAssocationTypes={props.filterUserbyAssocationTypes}
              userInfiniteScrollProps={props.userInfiniteScrollProps}
              tooltip={props.tooltip}
              isMulti={props.isMulti}
              suggestedProps={props.suggestedProps}
              displayPills
              id={props.id === 'request' ? 'request' : 'recipient'}
              getRecipientWarningMessage={(
                hasSelectedUsersNotInOrg: boolean,
              ): React.ReactNode => (
                <>
                  {props.isRequest ? (
                    <div
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                        padding: 10px 20px 0px 20px;
                      `}
                    >
                      {translate(
                        "You can only request from someone within your organization. If someone is missing, please make sure you're in the same organization.",
                      )}
                    </div>
                  ) : (
                    !isUpdateForm &&
                    hasSelectedUsersNotInOrg &&
                    !guestCardTypes.includes(props.typeofCard) && (
                      <SoftAlertBox
                        css={css`
                          margin-top: 20px;
                          padding: 12px 20px;
                        `}
                      >
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          {translate(
                            showGuestCopyMemberBK
                              ? "You've added a recipient outside of your org. By creating this card, they’ll be added as a Guest."
                              : showGuestCopyAdminOwner
                                ? "You've added a recipient outside of your org. Before the card is created, you'll be prompted to add this user as either a Guest or Member."
                                : "You've added a recipient outside of your org. By adding them, they'll also be invited to join your org.",
                          )}
                        </span>
                      </SoftAlertBox>
                    )
                  )}
                </>
              )}
              setSelectedUsersNotInOrg={setSelectedUsersNotInOrg}
              unableToAddNewUser={props.unableToAddNewUser}
            />
          </InputBoxDiv>
        ) : null}
        {isBillPay && (
          <>
            <InputBoxDiv
              css={css`
                z-index: 6;
              `}
              isDisabled={isDisabled}
              isLocked={isLocked}
              isGreyed={isGreyed}
              ref={
                inputBoxRefs?.[
                  VirtualCardFormInputBoxes.BillPayVendorNameInputBox
                ]
              }
            >
              <BillPayVendorNameInputBox
                id={'vendorName'}
                vendorNameProps={{
                  showUpdates: isUpdateForm,
                  value: flatFields.vendorName,
                  baseValue: props.updateProps?.baseFlatFields.vendorName,
                  errors: flatFieldsErrors.vendorName,
                  onChange: (val: string): void => {
                    onFlatFieldChange('vendorName', val)
                  },
                }}
                vendorNameFieldLabel={translate('Vendor Name')}
                static={isUpdateForm || isFinalCardApproverForm}
                staticEmail={props.virtualCard?.billPayVendorEmail}
              />
            </InputBoxDiv>
            {!isUpdateForm && !isFinalCardApproverForm && (
              <InputBoxDiv
                css={css`
                  z-index: 4;
                `}
                isDisabled={isDisabled}
                isLocked={isLocked}
                isGreyed={isGreyed}
                isError={
                  flatFieldsErrors.billPayRecipient &&
                  flatFieldsErrors.billPayRecipient.length > 0
                }
                ref={
                  inputBoxRefs?.[
                    VirtualCardFormInputBoxes.BillPayRecipientInputBox
                  ]
                }
              >
                <BillPayRecipientInputBox
                  billPayRecipientProps={{
                    value: flatFields.billPayRecipient,
                    errors: flatFieldsErrors.billPayRecipient,
                    onChange: (val: BillPayRecipient): void => {
                      onFlatFieldChange('billPayRecipient', val)
                      setFlatFieldsErrors({
                        ...flatFieldsErrors,
                        billPayRecipient: undefined,
                      })
                    },
                  }}
                  vendorEmailProps={{
                    value: flatFields.vendorEmail,
                    errors: flatFieldsErrors.vendorEmail,
                    onChange: (val: string): void => {
                      onFlatFieldChange('vendorEmail', val)
                    },
                  }}
                />
              </InputBoxDiv>
            )}
          </>
        )}
        {props.amountProps.amountEditable && (
          <InputBoxDiv
            css={css`
              z-index: 3;
            `}
            isDisabled={disableCreditAndAmountInputBox}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={
              inputBoxRefs?.[
                VirtualCardFormInputBoxes.CreditCardAndAmountInputBox
              ]
            }
          >
            <CreditCardAndAmountInputBox
              id={'account'}
              labelCreditCardsAsAccounts={labelCreditCardsAsAccounts}
              budgetGroupLabel={props.creditCardProps?.budgetGroupLabel}
              creditCardOptions={props.creditCardProps?.creditCardOptions}
              balanceLabel={getBalanceInputLabel()}
              limitLabel={getLimitInputLabel()}
              recurrenceLabel={
                isBillPay ? translate('Invoice Amount') : translate('Resets to')
              }
              creditCardIdProps={
                props.creditCardProps !== undefined
                  ? {
                      showUpdates: isUpdateForm,
                      value: flatFields.creditCardId,
                      errors: flatFieldsErrors.creditCardId,
                      onChange: (creditCardId: string): void => {
                        const newCard = creditCards[creditCardId]
                        const creditCardCurrency = newCard.currency
                        const clearExactPay =
                          !newCard.features?.singleExactPayEnabled
                        setFlatFields({
                          ...flatFields,
                          creditCardId: creditCardId,
                          currency: creditCardCurrency,
                          ...(clearExactPay && { singleExactPay: undefined }),
                          ...(flatFields.currency !== creditCardCurrency && {
                            balanceDollars: 0,
                          }),
                        })
                        props.onCreditCardChange?.(creditCardId)
                      },
                      isStatic: !props.creditCardEditable || isLocked,
                    }
                  : isFinalCardApproverForm
                    ? {
                        showUpdates: isUpdateForm,
                        value: props.virtualCard?.creditCardId,
                        errors: flatFieldsErrors.creditCardId,
                        onChange: (creditCardId: string): void => {
                          const newCard = creditCards[creditCardId]
                          const creditCardCurrency = newCard.currency
                          const clearExactPay =
                            !newCard.features?.singleExactPayEnabled
                          setFlatFields({
                            ...flatFields,
                            creditCardId: creditCardId,
                            currency: creditCardCurrency,
                            ...(clearExactPay && { singleExactPay: undefined }),
                            ...(flatFields.currency !== creditCardCurrency && {
                              balanceDollars: 0,
                            }),
                          })
                          props.onCreditCardChange?.(creditCardId)
                        },
                        isStatic: !props.creditCardEditable || isLocked,
                      }
                    : undefined
              }
              accountMessage={props.creditCardProps?.accountMessage}
              invoiceNumberProps={
                isBillPay
                  ? {
                      showUpdates: isUpdateForm,
                      baseValue:
                        props.updateProps?.baseFlatFields.invoiceNumber,
                      value: flatFields.invoiceNumber,
                      errors: flatFieldsErrors.invoiceNumber,
                      onChange: (val: string | undefined): void => {
                        onFlatFieldChange('invoiceNumber', val)
                      },
                    }
                  : undefined
              }
              invoiceDateMomentProps={
                isBillPay
                  ? {
                      showUpdates: isUpdateForm,
                      baseValue:
                        props.updateProps?.baseFlatFields.invoiceDateMoment,
                      value: flatFields.invoiceDateMoment,
                      errors: flatFieldsErrors.invoiceDateMoment,
                      onChange: (val: Moment | undefined): void => {
                        onFlatFieldChange('invoiceDateMoment', val)
                      },
                    }
                  : undefined
              }
              balanceDollarsProps={
                !isUpdateBillPay
                  ? {
                      showUpdates: isUpdateForm,
                      baseValue:
                        props.updateProps?.baseFlatFields.balanceDollars,
                      value: flatFields.balanceDollars,
                      errors: flatFieldsErrors.balanceDollars,
                      onChange: (val: number | undefined): void => {
                        onFlatFieldChange('balanceDollars', val)
                      },
                      isStatic:
                        (isUpdateForm || isFinalCardApproverForm) && isBillPay,
                    }
                  : undefined
              }
              limitDollarsProps={
                isUpdateBillPay || props.lockAmountOnly
                  ? {
                      showUpdates: isUpdateForm,
                      baseValue: props.updateProps?.baseFlatFields.limitDollars,
                      value: flatFields.limitDollars,
                      errors: flatFieldsErrors.limitDollars,
                      onChange: (val: number | undefined): void => {
                        onFlatFieldChange('limitDollars', val)
                      },
                      isStatic:
                        (isUpdateForm || isFinalCardApproverForm) && isBillPay,
                    }
                  : undefined
              }
              recurrenceDollarsProps={
                !isUpdateBillPay
                  ? {
                      showUpdates: isUpdateForm,
                      baseValue:
                        props.updateProps?.baseRecurrenceFields?.amountDollars,
                      value: recurrenceFields.recurrenceDollars,
                      errors: recurrenceFieldsErrors.recurrenceDollars,
                      onChange: (val: number | undefined): void => {
                        onRecurrenceFieldChange('recurrenceDollars', val)
                      },
                    }
                  : undefined
              }
              recurrenceValues={recurrenceFields}
              recurrenceErrors={recurrenceFieldsErrors}
              recurrenceChangeHandlers={
                props.recurrenceEditable
                  ? {
                      onRecurrenceDollarsChange: (val?: number): void => {
                        onRecurrenceFieldChange('recurrenceDollars', val)
                      },
                      onPeriodChange: (val: RecurrencePeriod): void => {
                        onRecurrenceFieldChange('period', val)
                      },
                      onIntervalChange: (val: number): void => {
                        onRecurrenceFieldChange('interval', val)
                      },
                      onTerminatorChange: (val: RecurrenceTerminator): void => {
                        onRecurrenceFieldChange('terminator', val)
                      },
                      onCountChange: (val: number): void => {
                        onRecurrenceFieldChange('count', val)
                      },
                      onUntilMomentChange: (val: Moment): void => {
                        onRecurrenceFieldChange('untilMoment', val)
                      },
                      onByWeekDayChange: (val: number): void => {
                        onRecurrenceFieldChange('byWeekDay', val)
                      },
                      onByMonthDayChange: (val: number): void => {
                        onRecurrenceFieldChange('byMonthDay', val)
                      },
                    }
                  : undefined
              }
              baseRecurrenceValues={props.updateProps?.baseRecurrenceFields}
              resetsOnMomentProps={props.resetsOnMomentProps}
              resetsOnHelperText={props.resetsOnHelperText}
              validToMomentProps={
                props.recurrenceEditable
                  ? undefined
                  : {
                      showUpdates: isUpdateForm,
                      baseValue:
                        props.updateProps?.baseFlatFields.validToMoment,
                      value: flatFields.validToMoment,
                      errors: flatFieldsErrors.validToMoment,
                      onChange: (val: Moment | undefined): void => {
                        onFlatFieldChange('validToMoment', val)
                      },
                      // Bulk update only; non-bulk falls back to SingleDateInputField's
                      // own 'Select a date' default (avoid duplicating it here).
                      placeHolder: isBulkUpdate
                        ? translateSingleString(
                            locale,
                            'Leave blank if no change is desired',
                          )
                        : undefined,
                    }
              }
              timezone={props.updateProps?.timezone}
              maxValue={99999999}
              currencyProps={{
                showUpdates: isUpdateForm,
                baseValue: props.updateProps?.baseFlatFields.currency,
                value: flatFields.currency,
                errors: flatFieldsErrors.currency,
                onChange: (val: string): void => {
                  onFlatFieldChange('currency', val)
                },
              }}
              creditCardInfiniteScrollProps={
                props.creditCardInfiniteScrollProps
              }
              isApproveRequestForm={isApproveRequestForm}
              isRequest={props.isRequest !== undefined && props.isRequest}
              isUpdateForm={isUpdateForm}
              amountPlaceholder={props.amountProps?.amountPlaceholder}
              optional={!!props.amountProps.amountEditableOptional}
              hideInputFieldsAndLock={isFinalCardApproverForm}
              lockAmountOnly={props.lockAmountOnly}
              hideTooltips={
                disableCreditAndAmountInputBox || isLocked || isGreyed
              }
              isBillPay={isBillPay}
              integerOnly={isIntegerOnly}
            />
          </InputBoxDiv>
        )}
        {lowBalanceAlertFeatureEnabled && (
          <InputBoxDiv
            isDisabled={isDisabled}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.LowBalanceAlertBox]}
          >
            <LowBalanceAlertField
              isEnabled={!!flatFields.lowBalanceAlertEnabled}
              onEnabledChange={(next: boolean): void => {
                setFlatFields({
                  ...flatFields,
                  lowBalanceAlertEnabled: next,
                  lowBalanceThresholdDollars: next
                    ? flatFields.lowBalanceThresholdDollars
                    : undefined,
                })
              }}
              thresholdDollars={flatFields.lowBalanceThresholdDollars}
              onThresholdChange={(val: number | undefined): void => {
                onFlatFieldChange('lowBalanceThresholdDollars', val)
              }}
              currency={flatFields.currency}
              readOnly={isLocked || isGreyed}
              error={flatFieldsErrors.lowBalanceThresholdDollars?.[0]}
            />
          </InputBoxDiv>
        )}
        {enableExactPay && (
          <InputBoxDiv
            isDisabled={disableExactPayBox}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.ExactPayBox]}
          >
            <ExactPayBox
              isSelected={flatFields.singleExactPay}
              onClick={(): void => {
                onFlatFieldChange('singleExactPay', !flatFields.singleExactPay)
              }}
              tooltip={<ExactPayTooltip openLeft={false} />}
            />
          </InputBoxDiv>
        )}
        {selectedCreditCard?.features.customAddress &&
          countrySchema !== undefined &&
          props.customAddressEditable && (
            <InputBoxDiv
              css={css`
                z-index: 2;
              `}
              isDisabled={isDisabled}
              isLocked={isLocked}
              isGreyed={isGreyed}
              ref={
                inputBoxRefs?.[
                  VirtualCardFormInputBoxes.CustomVirtualCardAddressBox
                ]
              }
            >
              <CustomVirtualCardAddressBox
                currentBillingAddress={
                  selectedCreditCard.address.fullAddressLabel
                }
                customBillingAddress={flatFields.customBillingAddress}
                countrySchema={countrySchema}
                onButtonSelect={(val: boolean): void => {
                  onFlatFieldChange('customBillingAddress', val)
                }}
                flatFields={customAddressFields}
                flatFieldsErrors={customAddressFieldsErrors}
                onFlatFieldChange={onCustomAddressFieldChange}
              />
            </InputBoxDiv>
          )}
        {selectedCreditCard?.features.plasticCardsEnabled &&
          countrySchema !== undefined &&
          props.physicalCardEditable && (
            <InputBoxDiv
              isDisabled={isDisabled}
              isLocked={isLocked}
              isGreyed={isGreyed}
              ref={
                inputBoxRefs?.[VirtualCardFormInputBoxes.PhysicalVirtualCardBox]
              }
            >
              <PhysicalVirtualCardBox
                includePhysicalCard={flatFields.includePhysicalCard}
                onCheckboxSelect={(val: boolean): void => {
                  onFlatFieldChange('includePhysicalCard', val)
                }}
                plasticFirstNameProps={
                  selectedUserHasName
                    ? undefined
                    : {
                        value: flatFields.plasticFirstName,
                        errors: flatFieldsErrors.plasticFirstName,
                        onChange: (val: string): void => {
                          onFlatFieldChange('plasticFirstName', val)
                        },
                      }
                }
                plasticLastNameProps={
                  selectedUserHasName
                    ? undefined
                    : {
                        value: flatFields.plasticLastName,
                        errors: flatFieldsErrors.plasticLastName,
                        onChange: (val: string): void => {
                          onFlatFieldChange('plasticLastName', val)
                        },
                      }
                }
                currentBillingAddress={
                  selectedCreditCard.address.fullAddressLabel
                }
                customShippingAddress={flatFields.physicalCustomShippingAddress}
                countrySchema={countrySchema}
                onButtonSelect={(val: boolean): void => {
                  onFlatFieldChange('physicalCustomShippingAddress', val)
                }}
                flatFields={physicalAddressFields}
                flatFieldsErrors={physicalAddressFieldsErrors}
                onFlatFieldChange={onPhysicalAddressFieldChange}
              />
            </InputBoxDiv>
          )}
        {showExpenseCategories && (
          <InputBoxDiv
            isDisabled={isDisabled}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={
              inputBoxRefs?.[
                VirtualCardFormInputBoxes.ExpenseCategoriesInputBox
              ]
            }
          >
            {(isLocked || isGreyed) && props.virtualCard !== undefined ? (
              <ExpenseCategoriesReadOnlyBox virtualCard={props.virtualCard} />
            ) : (
              <ExpenseCategoriesInputBox
                context={ExpenseCategoriesContext.VIRTUAL_CARD}
                showUpdates={isUpdateForm}
                hideWasOnUpdate={props.hideWasOnUpdate}
                explicitSelectNone={props.explicitSelectNone}
                initialExpenseDetails={initialExpenseCategoryObjects}
                expenseDetailsErrors={expenseCategoriesErrors}
                expenseDetails={expenseCategories}
                isRequest={isRequestForm}
                hasIntegrations={hasIntegrations}
                onExpenseDetailChange={(
                  categoryId: string,
                  labelId: string,
                ): void => {
                  onExpenseCategoryChange(categoryId, labelId)
                }}
                deselectExpenseDetail={(categoryId: string): void => {
                  const newExpenseCategories = { ...expenseCategories }
                  delete newExpenseCategories[categoryId]
                  setExpenseCategories(newExpenseCategories)
                }}
                closedByDefault={isLocked}
                isCardUpdateForm={isUpdateForm}
                requiresUpdateApproval={
                  !props.canUpdateVirtualCard &&
                  props.typeofCard !== VirtualCardTypes.bulk
                }
                testIdOptionBox={'expense-categories-box'}
              />
            )}
          </InputBoxDiv>
        )}
        {(!props.isRequest || props.canUpdateVirtualCard) &&
          user?.hasOrgBookkeeperPermissions &&
          orgReceiptRulesEnabled &&
          organization?.receiptRule?.enabled &&
          !props.virtualCard?.direct &&
          !isBillPay &&
          props.typeofCard !== VirtualCardTypes.bulk &&
          !guestCardTypes.includes(props.typeofCard) &&
          (flatFields.userEmails === undefined ||
            flatFields.userEmails?.length <= 1) && (
            <InputBoxDiv
              isDisabled={isDisabled}
              isLocked={isLocked}
              isGreyed={isGreyed}
              ref={
                inputBoxRefs?.[VirtualCardFormInputBoxes.ReceiptsRequiredBox]
              }
            >
              <ReceiptsRequiredBox
                receiptRulesExempt={flatFields.receiptRulesExempt}
                onChange={(val: boolean): void => {
                  onFlatFieldChange('receiptRulesExempt', val)
                }}
                virtualCardCurrency={virtualCardCurrency}
                receiptThresholdCents={
                  virtualCardCurrency === ExtendCurrency.Cad
                    ? organization?.receiptRule?.cadThresholdCents
                    : organization?.receiptRule?.usdThresholdCents
                }
                showNote={isUpdateForm}
              />
            </InputBoxDiv>
          )}
        {props.attachmentsProps !== undefined && showAttachmentsBox && (
          <InputBoxDiv
            isDisabled={isDisabled}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.AttachmentsInputBox]}
          >
            <AttachmentsInputBox
              sectionHeader={props.attachmentsProps.sectionHeader}
              sectionSubheader={props.attachmentsProps.sectionSubheader}
              receiptAttachments={props.attachmentsProps.receiptAttachments}
              uploadingAttachmentThumbnails={
                props.attachmentsProps?.uploadingAttachmentThumbnails
              }
              uploadAttachments={
                !isFinalCardApproverForm
                  ? props.attachmentsProps.uploadAttachments
                  : undefined
              }
              deleteAttachment={props.attachmentsProps.deleteAttachment}
              id={'attachmentsDocs'}
            />
          </InputBoxDiv>
        )}
        {showNotesBox && (
          <InputBoxDiv
            isDisabled={isDisabled}
            isLocked={isLocked}
            isGreyed={isGreyed}
            ref={inputBoxRefs?.[VirtualCardFormInputBoxes.NotesInputBox]}
          >
            {(isGreyed || isLocked) && flatFields.notes !== undefined ? (
              <ReadOnlyNotesBox notes={flatFields.notes} />
            ) : (
              <NotesInputBox
                notesProps={{
                  showUpdates: isUpdateForm,
                  baseValue: props.updateProps?.baseFlatFields.notes,
                  value: flatFields.notes,
                  onChange: (val: string): void => {
                    onFlatFieldChange('notes', val)
                  },
                }}
                collapsible
                id={'notes'}
              />
            )}
          </InputBoxDiv>
        )}
        {((props.showCardApprovalSoftAlert && !isLocked) ||
          isFinalCardApproverForm) &&
          !isLockedNonCardApproverForm && (
            <>
              <SoftAlertBox
                css={css`
                  border-radius: 6px;
                  margin-top: 20px;
                  padding: 16px 20px;
                  background-color: #fff4cf;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
                testId={'cardApproverSoftAlertBox'}
              >
                {isFinalCardApproverForm
                  ? isBillPay
                    ? props.virtualCard?.billPayVendorEmail !== undefined
                      ? translate(
                          'You are the final Card Approver. Once you approve this Bill Pay card, it will be sent to the vendor',
                        )
                      : translate(
                          'You are the final Card Approver. Once you approve this Bill Pay card, it will be sent to the submitter',
                        )
                    : translate(
                        'You are the final Card Approver. Once you approve this card, it will be sent to the recipient',
                      )
                  : isApproveUpdateForm && !isFinalCardApproverForm
                    ? translate(
                        "Once you approve this update, if the card limit is above the organization's threshold amount, it will go to the Card Approver for final approval",
                      )
                    : isApproveRequestForm && !isFinalCardApproverForm
                      ? translate(
                          "Once you approve this request, if the card limit is above the organization's threshold amount, it will go to the Card Approver for final approval",
                        )
                      : isUpdateForm
                        ? translate(
                            "If the updated card limit is above the organization's threshold amount, it will require final approval from the Card Approver",
                          )
                        : translate(
                            "If this card is above the organization's threshold amount, it will require final approval from the Card Approver",
                          )}
              </SoftAlertBox>
            </>
          )}
        {isCreateBillPay &&
          flatFields.billPayRecipient === BillPayRecipient.Vendor &&
          selectedIssuer?.recipientScreeningEnabled && (
            <BillPaySanctionsScreeningNote />
          )}
        {props.topLevelError !== undefined && (
          <span
            css={css`
              margin-top: 40px;
              ${getFontCss(
                14,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-top: 30px;
              `)}
            `}
            data-testid={'virtualCardFormError'}
          >
            {props.errorDetailMap
              ? props.topLevelError +
                ': ' +
                props.errorDetailMap[Object.keys(props.errorDetailMap)[0]][0]
              : props.topLevelError}
          </span>
        )}
        {!isLockedNonCardApproverForm && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              margin-top: 40px;
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-top: 30px;
              `)}
            `}
          >
            <Button
              id={props.id ? props.id + 'Button' : 'Button'}
              testId={props.id ? props.id + 'Button' : 'Button'}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.submit}
              big={true}
              text={getButtonText()}
              loading={props.loading}
              disabled={props.loading || isDisabled}
            />
            {props.negativeButton !== undefined && (
              <span
                onKeyPress={(): null => {
                  return null
                }}
                role="button"
                tabIndex={0}
              >
                <Button
                  id={'rejectButton'}
                  design={ButtonDesigns.designRed}
                  type={ButtonTypes.button}
                  big={true}
                  text={props.negativeButton.text}
                  onClick={props.negativeButton?.onClick}
                  loading={props.negativeButton.loading}
                />
              </span>
            )}
          </div>
        )}
      </form>
    </>
  )
}

export default VirtualCardForm
